import { useState, useEffect, useRef } from "@wordpress/element";
import {
	X,
	Play,
	Pause,
	Volume2,
	ChevronRight,
	ChevronLeft,
} from "lucide-react";
import { speak } from "@/lib/feature-style";
import { useTranslation } from "@/hooks/use-translation";

const DictionaryModal = ({ isOpen, onClose, initialWord = "" }) => {
	const { __ } = useTranslation();
	const [word, setWord] = useState(initialWord);
	const [definitions, setDefinitions] = useState([]);
	const [currentPage, setCurrentPage] = useState(0);
	const [loading, setLoading] = useState(false);
	const [speaking, setSpeaking] = useState(false);
	const inputRef = useRef(null);

	// Focus input when modal opens
	useEffect(() => {
		if (isOpen && inputRef.current) {
			setTimeout(() => {
				inputRef.current.focus();
			}, 100);
		}
	}, [isOpen]);

	// Set initial word when provided
	useEffect(() => {
		if (initialWord && initialWord !== word) {
			setWord(initialWord);
			if (initialWord.trim()) {
				fetchDefinitions(initialWord.trim());
			}
		}
	}, [initialWord]);

	// Debounced search
	useEffect(() => {
		const timer = setTimeout(() => {
			if (word.trim()) {
				fetchDefinitions(word.trim());
			} else {
				setDefinitions([]);
				setCurrentPage(0);
			}
		}, 500);

		return () => clearTimeout(timer);
	}, [word]);

	const fetchDefinitions = async (searchWord) => {
		setLoading(true);
		setDefinitions([]);
		setCurrentPage(0);

		try {
			// First try WordPress API endpoint
			let response = await fetch(
				`${
					window.location.origin
				}/wp-json/zn7/v1/dictionary/${encodeURIComponent(
					searchWord.toLowerCase(),
				)}`,
			);

			// If WordPress API is not available (404), try external API directly
			if (!response.ok && response.status === 404) {
				console.warn(
					"WordPress dictionary API not available, using external API directly",
				);
				response = await fetch(
					`https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(
						searchWord.toLowerCase(),
					)}`,
				);
			}

			if (response.ok) {
				const data = await response.json();

				// Check if there's an error response from our API
				if (data.error) {
					setDefinitions([
						{
							type: "Error",
							definition: data.error,
						},
					]);
					return;
				}

				const allDefinitions = [];

				// Extract all definitions from the API response (same format as external API)
				data.forEach((entry) => {
					entry.meanings.forEach((meaning) => {
						meaning.definitions.forEach((def) => {
							allDefinitions.push({
								type: meaning.partOfSpeech,
								definition: def.definition,
								example: def.example || null,
							});
						});
					});
				});

				setDefinitions(allDefinitions);
			} else {
				// Handle HTTP error responses
				const errorData = await response
					.json()
					.catch(() => ({ error: "Unknown error" }));
				setDefinitions([
					{
						type: "Error",
						definition:
							errorData.error || `No definition found for "${searchWord}".`,
					},
				]);
			}
		} catch (error) {
			console.error("Dictionary API error:", error);

			// Fallback: Try external API directly as last resort
			try {
				const fallbackResponse = await fetch(
					`https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(
						searchWord.toLowerCase(),
					)}`,
				);

				if (fallbackResponse.ok) {
					const data = await fallbackResponse.json();
					const allDefinitions = [];

					data.forEach((entry) => {
						entry.meanings.forEach((meaning) => {
							meaning.definitions.forEach((def) => {
								allDefinitions.push({
									type: meaning.partOfSpeech,
									definition: def.definition,
									example: def.example || null,
								});
							});
						});
					});

					setDefinitions(allDefinitions);
				} else {
					throw new Error("External API also failed");
				}
			} catch (fallbackError) {
				console.error("Fallback dictionary API also failed:", fallbackError);
				setDefinitions([
					{
						type: "Error",
						definition: `Unable to fetch definition. Please check your internet connection.`,
					},
				]);
			}
		} finally {
			setLoading(false);
		}
	};

	const handleReadDefinition = () => {
		if (speaking) {
			// Stop speaking
			if (window.speechSynthesis.speaking) {
				window.speechSynthesis.cancel();
			}
			setSpeaking(false);
		} else {
			// Start speaking
			if (definitions.length > 0 && definitions[currentPage]) {
				const textToRead = `${definitions[currentPage].type}. ${definitions[currentPage].definition}`;
				speak(textToRead, () => {
					setSpeaking(false);
				});
				setSpeaking(true);
			}
		}
	};

	const handleSpellWord = () => {
		if (!word.trim()) return;

		// Stop any current speech
		if (window.speechSynthesis.speaking) {
			window.speechSynthesis.cancel();
		}
		setSpeaking(false);

		// Spell each letter with a pause
		const letters = word.trim().split("");
		let letterIndex = 0;

		const spellNextLetter = () => {
			if (letterIndex < letters.length) {
				const letter = letters[letterIndex];
				const utterance = new SpeechSynthesisUtterance(letter);
				utterance.rate = 0.8;
				utterance.onend = () => {
					letterIndex++;
					setTimeout(spellNextLetter, 300);
				};
				window.speechSynthesis.speak(utterance);
			}
		};

		spellNextLetter();
	};

	const changePage = (direction) => {
		if (definitions.length <= 1) return;

		let newPage = currentPage + direction;
		if (newPage < 0) newPage = definitions.length - 1;
		if (newPage >= definitions.length) newPage = 0;

		setCurrentPage(newPage);
		setSpeaking(false); // Stop speaking when changing pages
	};

	const handleClose = () => {
		// Stop any speaking
		if (window.speechSynthesis.speaking) {
			window.speechSynthesis.cancel();
		}
		setSpeaking(false);
		onClose();
	};

	// Handle escape key
	useEffect(() => {
		const handleEscape = (e) => {
			if (e.key === "Escape" && isOpen) {
				handleClose();
			}
		};

		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [isOpen]);

	if (!isOpen) return null;

	const currentDef = definitions[currentPage];

	return (
		<div className="zn:fixed zn:inset-0  zn:flex zn:items-center zn:justify-center zn:z-[100001]">
			<div className="zn:!bg-white zn:rounded-xl zn:!p-6 zn:w-[400px] zn:max-w-[90vw] zn:!shadow-xl">
				{/* Header */}
				<div className="zn:flex zn:justify-between zn:items-center zn:!mb-5 ">
					<div className="zn:text-xl zn:font-semibold zn:text-gray-900">
						{__("Dictionary", "easeaccess")}
					</div>
					<button
						onClick={handleClose}
						className="zn:text-gray-400 zn:hover:text-gray-600 zn:text-2xl zn:leading-none"
					>
						<X className="zn:size-6" />
					</button>
				</div>

				{/* Input */}
				<div className="zn:!mb-5">
					<input
						ref={inputRef}
						type="text"
						value={word}
						onChange={(e) => setWord(e.target.value)}
						placeholder={__("Type a word...", "easeaccess")}
						className="zn:w-full zn:!px-3 zn:py-2 zn:!border zn:!border-gray-300 zn:rounded-md zn:focus:outline-none zn:focus:ring-2 zn:focus:ring-blue-500 zn:focus:border-transparent"
					/>
				</div>

				{/* Content */}
				<div className="zn:min-h-[150px] zn:mb-5 zn:p-4 zn:bg-gray-50 zn:rounded-md">
					{loading ? (
						<p className="zn:text-gray-600 zn:text-center">
							{__("Loading definitions...", "easeaccess")}
						</p>
					) : definitions.length === 0 ? (
						<p className="zn:text-gray-600 zn:text-center">
							{__(
								"Type or select a word to see its definition",
								"easeaccess",
							)}
						</p>
					) : currentDef ? (
						<div>
							<div className="zn:mb-3">
								<span className="zn:bg-blue-600 zn:text-white zn:px-2 zn:py-1 zn:rounded zn:text-sm">
									{currentDef.type}
								</span>
							</div>
							<p className="zn:text-gray-900 zn:leading-relaxed zn:mb-0">
								{currentDef.definition}
							</p>
							{currentDef.example && (
								<p className="zn:mt-3 zn:p-3 zn:bg-gray-100 zn:border-l-3 zn:border-blue-600 zn:italic zn:text-gray-700 zn:text-sm">
									{__("Example:", "easeaccess")} {currentDef.example}
								</p>
							)}
						</div>
					) : null}
				</div>

				{/* Pagination */}
				{definitions.length > 1 && (
					<div className="zn:flex zn:justify-center zn:items-center zn:gap-4 zn:!mb-5">
						<span
							onClick={() => changePage(-1)}
							className="zn:px-3 zn:py-1 zn:!bg-gray-200 zn:hover:bg-gray-300 zn:rounded zn:transition"
						>
							<ChevronLeft className="zn:size-4" />
						</span>
						<span className="zn:text-sm zn:text-gray-600">
							{currentPage + 1} / {definitions.length}
						</span>
						<span
							onClick={() => changePage(1)}
							className="zn:px-3 zn:py-1 zn:!bg-gray-200 zn:hover:bg-gray-300 zn:rounded zn:transition"
						>
							<ChevronRight className="zn:size-4" />
						</span>
					</div>
				)}

				{/* Action Buttons */}
				<div className="zn:flex zn:gap-3">
					<span
						onClick={handleReadDefinition}
						disabled={loading || definitions.length === 0}
						className="zn:flex-1 zn:!px-4 zn:!py-2 zn:!bg-primary zn:text-white zn:rounded-md zn:hover:bg-primary/80 zn:disabled:opacity-50 zn:disabled:cursor-not-allowed zn:flex zn:items-center zn:justify-center zn:gap-2 zn:transition zn:cursor-pointer"
					>
						{speaking ? (
							<>
								<Pause className="zn:size-4" />
								{__("Pause", "easeaccess")}
							</>
						) : (
							<>
								<Play className="zn:size-4" />
								{__("Read", "easeaccess")}
							</>
						)}
					</span>
					<span
						onClick={handleSpellWord}
						disabled={loading || !word.trim()}
						className="zn:flex-1 zn:!px-4 zn:!py-2 zn:!bg-success zn:text-white zn:rounded-md zn:hover:bg-success/80 zn:disabled:opacity-50 zn:disabled:cursor-not-allowed zn:flex zn:items-center zn:justify-center zn:gap-2 zn:transition zn:cursor-pointer"
					>
						<Volume2 className="zn:size-4" />
						{__("Spell", "easeaccess")}
					</span>
				</div>
			</div>
		</div>
	);
};

export default DictionaryModal;
